import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import type { PersonalOfficeDoorStatus } from "@/lib/enums";
import {
  DEFAULT_OFFICE_THEME,
  normalizeOfficeTheme,
  type OfficeTheme,
} from "@/lib/office/office-theme";
import { InWorldSheet } from "./InWorldSheet";
import { OfficeStudio, type OfficeStudioValues } from "./OfficeStudio";

type CustomizeOfficeDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  displayName: string;
  doorStatus?: PersonalOfficeDoorStatus;
  decorIds: string[];
  wallArtFileId?: string | null;
  wallArtPlaceholder?: string | null;
  theme?: OfficeTheme | null;
  unlockedIds: string[];
  points: number;
  isSaving: boolean;
  onSave: (input: OfficeStudioValues) => void;
};

/**
 * In-world office customize sheet (keeps HQ map visible on the left).
 * Named *Dialog for call-site stability; UI is a side sheet, not a modal.
 */
export function CustomizeOfficeDialog({
  open,
  onOpenChange,
  displayName,
  doorStatus = "closed",
  decorIds,
  wallArtFileId = null,
  wallArtPlaceholder = null,
  theme = null,
  unlockedIds,
  points,
  isSaving,
  onSave,
}: CustomizeOfficeDialogProps) {
  const [values, setValues] = useState<OfficeStudioValues>({
    displayName,
    doorStatus,
    decorIds,
    wallArtFileId,
    wallArtPlaceholder,
    theme: normalizeOfficeTheme(theme ?? DEFAULT_OFFICE_THEME),
  });

  useEffect(() => {
    if (open) {
      setValues({
        displayName,
        doorStatus,
        decorIds,
        wallArtFileId,
        wallArtPlaceholder,
        theme: normalizeOfficeTheme(theme ?? DEFAULT_OFFICE_THEME),
      });
    }
  }, [open, displayName, doorStatus, decorIds, wallArtFileId, wallArtPlaceholder, theme]);

  useEffect(() => {
    if (!open) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") onOpenChange(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  return (
    <InWorldSheet
      open={open}
      title="Customize your office"
      subtitle="Paint, furnish, hang a photo — teammates see it live after save."
      onClose={() => onOpenChange(false)}
      wide
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            disabled={isSaving || values.displayName.trim().length === 0}
            onClick={() =>
              onSave({
                ...values,
                displayName: values.displayName.trim(),
                theme: normalizeOfficeTheme(values.theme),
              })
            }
          >
            {isSaving ? "Saving…" : "Save office"}
          </Button>
        </>
      }
    >
      <OfficeStudio
        values={values}
        onChange={(patch) => setValues((prev) => ({ ...prev, ...patch }))}
        unlockedIds={unlockedIds}
        points={points}
        disabled={isSaving}
        showPreview
        layout="tray"
      />
    </InWorldSheet>
  );
}
